import React from "react";
import { Alert, Container } from "../../../components";
import { ExpandableCard, Heading, Icon, Link, List, Text } from "@telia/teddy";
import _compact from "lodash/compact";
import _get from "lodash/get";
import { connect, useDispatch } from "react-redux";
import { MaybeEmpty } from "../../../../../constants";
import {
    ExistingAgreement,
    ExistingService,
    ExistingSubscription,
    OfferingCode,
    RootState,
} from "../../../../../models";
import { Account, TransferOffering } from "../../../../../models/state";
import { setSelectedAccount } from "../../../checkout/state/actions/accountActions";
import { setOfferingCode } from "../../../checkout/state/actions/transferOwnershipActions";
import {
    getRelatedAccounts,
    getSelectedAccount,
    getTransferExistingSwitchAgreement,
    getSelectedOfferingCode,
    isAllowedToKeepExistingSubscription as isAllowedToKeepExistingSubscriptionSelector,
    getTransferExistingLeaseAgreements,
    getTransferExistingSubscription,
    getTransferExistingInsurance,
    getTransferExistingServicesOnBAN,
    getTransferExistingOrganizationName,
} from "../../../checkout/state/selectors/accountSelectors";
import { getOfferingsForCart } from "../../../checkout/state/selectors/offeringsSelectors";
import {
    isAllowedToProceed,
    isCreditCheckRequired,
    isCreditCheckPerformed,
} from "../../../checkout/state/selectors/verificationSelectors";
import Agreement from "../../components/molecules/Agreement/Agreement";
import KeepAgreementCheckbox from "../../components/molecules/KeepAgreementCheckbox/KeepAgreementCheckbox";
import RelatedAccounts from "../../components/molecules/RelatedAccounts/RelatedAccounts";
import RelatedServices from "../../components/molecules/RelatedServices/RelatedServices";
import SubscriptionInformation from "../../components/molecules/SubscriptionInformation/SubscriptionInformation";
import TRANSFER_TEXT from "../../text-consts";
import { convertAgreementToBoxData, isSwitchAgreement } from "../../utils";
import "./Summary.less";

type SummaryProps = StateProps;

const Summary: React.FC<SummaryProps> = ({
    // Redux (data)
    relatedAccounts,
    selectedAccount,
    organizationName,
    hasExistingSwitchAgreementWithTF,
    selectedOfferingCode,
    isCreditCheckDenied,
    transferSubscriptions,
    isAllowedToKeepExistingSubscription,
    existingSwitchAgreement,
    existingLeaseAgreements,
    existingSubscription,
    existingServicesOnBAN,
    existingInsurance,
}) => {
    const dispatch = useDispatch();
    const shouldShowRelatedAccounts = relatedAccounts.length > 0;
    const isB2BTransfer = !!organizationName;
    const isNewSubscriptionPossible = (isB2BTransfer || !isAllowedToKeepExistingSubscription) && !isCreditCheckDenied;

    const existingAgreements = _compact([existingSwitchAgreement, ...(existingLeaseAgreements || [])]);
    const hasAgreements = existingAgreements.length > 0;

    const canKeepAgreement = existingAgreements.some(
        (agreement) => isSwitchAgreement(agreement.type) || !agreement.commitmentEndDate,
    );

    return (
        <>
            <Container className="tlo-summary tlo-summary__shadow" size="medium" noMargin whiteBg>
                {!isB2BTransfer && (
                    <>
                        <SubscriptionInformation heading={TRANSFER_TEXT.SUMMARY.HEADING.MAIN_B2C} showExistingOwner />
                        <RelatedServices services={existingServicesOnBAN} />
                        {hasAgreements && (
                            <div className="tlo-summary__related-phone">
                                <Heading
                                    mb="100"
                                    as="h4"
                                    variant="title-300"
                                    className="tlo-summary__related-phone-title"
                                >
                                    {"Tilhørende telefon"}
                                </Heading>
                                {canKeepAgreement && <span>{TRANSFER_TEXT.SUMMARY.AGREEMENT.CAN_KEEP}</span>}
                            </div>
                        )}
                        {existingAgreements.map((agreement, index) => (
                            <Agreement
                                key={index}
                                {...convertAgreementToBoxData(agreement)}
                                existingInsurance={existingInsurance}
                                footer={
                                    canKeepAgreement ? (
                                        <KeepAgreementCheckbox
                                            agreementType={agreement.type}
                                            legalOwner={existingSubscription.legalOwner || "Nåværende eier"}
                                            insuranceType={existingInsurance?.type}
                                        />
                                    ) : (
                                        <></>
                                    )
                                }
                            />
                        ))}
                    </>
                )}
                {isCreditCheckDenied && <Alert kind="info">{TRANSFER_TEXT.SUMMARY.CREDIT_CHECK_DENIED}</Alert>}
                {isNewSubscriptionPossible && (
                    <>
                        <Heading mb="100" as="h2" variant="title-200" className="tlo-summary__table-heading">
                            {TRANSFER_TEXT.SUMMARY.HEADING.MAIN_B2B}
                        </Heading>
                        <ExpandableCard
                            className="tlo-summary__subscriptions"
                            type="single"
                            value={selectedOfferingCode}
                            onValueChange={(value) => {
                                if (value !== selectedOfferingCode) {
                                    dispatch(setOfferingCode(value as OfferingCode));
                                }
                            }}
                        >
                            {transferSubscriptions.map((sub) => (
                                <ExpandableCard.Item key={sub.id} value={sub.id} className="tlo-summary__subscription">
                                    <ExpandableCard.Trigger>
                                        {sub.name && (
                                            <Text as="p" variant="paragraph-100">
                                                {sub.name}
                                            </Text>
                                        )}
                                        <ExpandableCard.Header>
                                            <ExpandableCard.Description>
                                                <Heading mb="100" as="h3" variant="title-200">
                                                    {sub.header}
                                                </Heading>
                                            </ExpandableCard.Description>
                                            <ExpandableCard.Indicator />
                                        </ExpandableCard.Header>
                                        <ExpandableCard.Price>
                                            <Heading mb="100" mr="50" as="h3" variant="title-200">
                                                {sub.price},-
                                            </Heading>
                                            <Text>{sub.priceInfo.join(" ")}</Text>
                                        </ExpandableCard.Price>
                                    </ExpandableCard.Trigger>
                                    <ExpandableCard.Content>
                                        <div dangerouslySetInnerHTML={{ __html: sub.disclaimer || "" }} />
                                    </ExpandableCard.Content>
                                </ExpandableCard.Item>
                            ))}
                        </ExpandableCard>
                    </>
                )}
                <section>
                    <Heading as="h2" mb="100" variant="title-100">
                        {TRANSFER_TEXT.SUMMARY.DISCLAIMER_LINKS.HEADING}
                    </Heading>
                    <List layout="vertical" gapSize="small">
                        <List.Item>
                            <List.Illustration variant="image">
                                <Icon name="chevron-right" aria-hidden="true" />
                            </List.Illustration>
                            <List.Content>
                                <Link
                                    variant="text"
                                    href="https://www.telia.no/globalassets/pdf/vilkar/telias-brukervilkar-for-privatpersoner.pdf"
                                    target="_blank"
                                >
                                    {TRANSFER_TEXT.SUMMARY.DISCLAIMER_LINKS.FIRST}
                                </Link>
                            </List.Content>
                        </List.Item>
                        {hasExistingSwitchAgreementWithTF && (
                            <>
                                <List.Item>
                                    <List.Illustration variant="image">
                                        <Icon name="chevron-right" aria-hidden="true" />
                                    </List.Illustration>
                                    <List.Content>
                                        <Link
                                            variant="text"
                                            href="https://www.telia.no/globalassets/pdf/vilkar/vilkar-og-betingelser-for-avtale-om-leie-av-mobiltelefon-leieavtale-tfnosl-2020-02.pdf"
                                            target="_blank"
                                        >
                                            {TRANSFER_TEXT.SUMMARY.DISCLAIMER_LINKS.SECOND}
                                        </Link>
                                    </List.Content>
                                </List.Item>
                                <List.Item>
                                    <List.Illustration variant="image">
                                        <Icon name="chevron-right" aria-hidden="true" />
                                    </List.Illustration>
                                    <List.Content>
                                        <Link
                                            variant="text"
                                            href="https://www.telia.no/globalassets/pdf/vilkar/telia-norge-svitsj-vilkar-og-betingelser-for-svitsj-2020-02.pdf"
                                            target="_blank"
                                        >
                                            {TRANSFER_TEXT.SUMMARY.DISCLAIMER_LINKS.THIRD}
                                        </Link>
                                    </List.Content>
                                </List.Item>
                                <List.Item>
                                    <List.Illustration variant="image">
                                        <Icon name="chevron-right" aria-hidden="true" />
                                    </List.Illustration>
                                    <List.Content>
                                        <Link
                                            variant="text"
                                            href="https://www.telia.no/globalassets/pdf/forsikring/svitsj-vilkar-2019/"
                                            target="_blank"
                                        >
                                            {TRANSFER_TEXT.SUMMARY.DISCLAIMER_LINKS.FOURTH}
                                        </Link>
                                    </List.Content>
                                </List.Item>
                            </>
                        )}
                    </List>
                </section>
            </Container>
            <Container className="tlo-summary" size="medium" noMargin>
                {shouldShowRelatedAccounts && (
                    <div className="tlo-summary__related-accounts__container">
                        <RelatedAccounts
                            relatedAccounts={relatedAccounts}
                            selectedAccountId={selectedAccount}
                            setSelectedAccountId={(accountId) => dispatch(setSelectedAccount(accountId))}
                        />
                    </div>
                )}
            </Container>
        </>
    );
};

type StateProps = {
    existingInsurance?: ExistingService;
    existingLeaseAgreements: ExistingAgreement[] | null;
    existingServicesOnBAN: ExistingService[];
    existingSubscription: MaybeEmpty<ExistingSubscription>;
    existingSwitchAgreement?: ExistingAgreement;
    hasExistingSwitchAgreementWithTF: boolean;
    isAllowedToKeepExistingSubscription: boolean;
    isCreditCheckDenied: boolean;
    organizationName: string;
    relatedAccounts: Account[];
    selectedAccount: string;
    selectedOfferingCode: string;
    transferSubscriptions: TransferOffering[];
};
const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    relatedAccounts: getRelatedAccounts(state),
    selectedAccount: getSelectedAccount(state),
    organizationName: getTransferExistingOrganizationName(state) || "",
    hasExistingSwitchAgreementWithTF:
        _get(getTransferExistingSwitchAgreement(state), "type") === "SWITCH_FLEX_TF_B2C_EXISTING",
    selectedOfferingCode: getSelectedOfferingCode(state),
    isCreditCheckDenied: !isAllowedToProceed(state) && isCreditCheckRequired(state) && isCreditCheckPerformed(state),
    transferSubscriptions: getOfferingsForCart(state) as TransferOffering[],
    isAllowedToKeepExistingSubscription: isAllowedToKeepExistingSubscriptionSelector(state),
    existingSwitchAgreement: getTransferExistingSwitchAgreement(state),
    existingLeaseAgreements: getTransferExistingLeaseAgreements(state),
    existingSubscription: getTransferExistingSubscription(state),
    existingServicesOnBAN: getTransferExistingServicesOnBAN(state),
    existingInsurance: getTransferExistingInsurance(state),
});

export default connect(mapStateToProps)(Summary);
